/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Injectable} from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import {Observable, ReplaySubject, Subject} from 'rxjs';
import {UserService} from './shared/services/user.service';
import {Permission} from './shared/utils/permission.enum';
import {ErrorPageEnum} from './shared/error-page/error-page.enum';
import {DependencyLoadNotifierService} from './shared/services/dependency-load-notifier/dependency-load-notifier.service';

@Injectable({
    providedIn: 'root'
})
export class PermissionsGuard  {
    canActivateChild = this.canActivate;

    constructor(private userService: UserService,
                private dependencyLoadNotifier: DependencyLoadNotifierService,
                private router: Router) {
    }

    canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
        let requiredPermissions: Permission[];

        if (next == null || next.data == null) {
            requiredPermissions = [];
        } else {
            requiredPermissions = next.data['permissions'];
        }

        const isAllowedSubject = new ReplaySubject<boolean>(1);

        this.dependencyLoadNotifier.onReady.subscribe(() => {
            this.emitIsAllowed(requiredPermissions, isAllowedSubject);
        });

        return isAllowedSubject.asObservable();
    }

    private emitIsAllowed(requiredPermissions: Permission[], isAllowedSubject: Subject<boolean>): void {
        if (this.userService.isFirstAccess) {
            this.router.navigate(['/', 'welcome-page']);

            isAllowedSubject.next(false);
        } else {
            const hasPermissions = this.userService.hasPermissions(requiredPermissions);

            if (!hasPermissions) {
                this.router.navigate(['/', 'error'], {queryParams: {errorType: ErrorPageEnum.FORBIDDEN}});
            }
            isAllowedSubject.next(hasPermissions);
        }
        isAllowedSubject.complete();
    }
}
